import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { useCreateCreatorProgram, useUpdateCreatorProgram } from '@/hooks/use-creators'
import type { CreatorProgram } from '@/lib/types'

const formSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  description: z.string().optional(),
  rewardType: z.enum(['percentage', 'fixed']),
  rewardValue: z.number().positive('Valor deve ser maior que 0'),
  warrantyDays: z.number().int().min(1).max(30).optional(),
  minOrderValue: z.number().positive().optional().or(z.literal(0)),
  maxRewardValue: z.number().positive().optional().or(z.literal(0)),
  isActive: z.boolean(),
  // Platform config
  guruMaxSubscriptionCycles: z.number().int().positive().optional().or(z.literal(0)),
  yampiOncePerCustomer: z.boolean().optional(),
  yampiAccumulate: z.boolean().optional(),
  yampiIgnorePromotionProducts: z.boolean().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface ProgramFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  program: CreatorProgram | null
}

export function ProgramFormDialog({ open, onOpenChange, program }: ProgramFormDialogProps) {
  const isEditing = !!program
  const createProgram = useCreateCreatorProgram()
  const updateProgram = useUpdateCreatorProgram()

  const form = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      name: '',
      description: '',
      rewardType: 'percentage',
      rewardValue: 5,
      warrantyDays: 7,
      minOrderValue: 0,
      maxRewardValue: 0,
      isActive: true,
      guruMaxSubscriptionCycles: 0,
      yampiOncePerCustomer: true,
      yampiAccumulate: false,
      yampiIgnorePromotionProducts: false,
    },
  })

  useEffect(() => {
    if (open && program) {
      form.reset({
        name: program.name,
        description: program.description ?? '',
        rewardType: program.rewardType,
        rewardValue: program.rewardValue,
        warrantyDays: program.warrantyDays,
        minOrderValue: program.minOrderValue ?? 0,
        maxRewardValue: program.maxRewardValue ?? 0,
        isActive: program.isActive,
        guruMaxSubscriptionCycles: program.platformConfig?.guru?.maxSubscriptionCycles ?? 0,
        yampiOncePerCustomer: program.platformConfig?.yampi?.oncePerCustomer ?? true,
        yampiAccumulate: program.platformConfig?.yampi?.accumulate ?? false,
        yampiIgnorePromotionProducts:
          program.platformConfig?.yampi?.ignorePromotionProducts ?? false,
      })
    } else if (open) {
      form.reset()
    }
  }, [open, program, form])

  const onSubmit = form.handleSubmit(async (values) => {
    const platformConfig = {
      guru:
        values.guruMaxSubscriptionCycles && values.guruMaxSubscriptionCycles > 0
          ? { maxSubscriptionCycles: values.guruMaxSubscriptionCycles }
          : undefined,
      yampi: {
        oncePerCustomer: values.yampiOncePerCustomer ?? true,
        accumulate: values.yampiAccumulate ?? false,
        ignorePromotionProducts: values.yampiIgnorePromotionProducts ?? false,
      },
    }

    const payload = {
      name: values.name,
      description: values.description || undefined,
      rewardType: values.rewardType,
      rewardValue: values.rewardValue,
      warrantyDays: values.warrantyDays,
      minOrderValue:
        values.minOrderValue && values.minOrderValue > 0 ? values.minOrderValue : undefined,
      maxRewardValue:
        values.maxRewardValue && values.maxRewardValue > 0 ? values.maxRewardValue : undefined,
      platformConfig,
      isActive: values.isActive,
    }

    if (isEditing) {
      await updateProgram.mutateAsync({ id: program.id, data: payload })
    } else {
      await createProgram.mutateAsync(payload)
    }
    onOpenChange(false)
  })

  const rewardType = useWatch({ control: form.control, name: 'rewardType' })
  const isPending = createProgram.isPending || updateProgram.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Editar Programa' : 'Novo Programa'}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? 'Alterar config pode disparar sync de cupons nos creators vinculados.'
              : 'Defina o desconto e as regras do programa de cupons.'}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
            <SheetBody className="space-y-4">
              {/* Basic info */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Padrão 5%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Input placeholder="Programa padrão para creators" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Reward config */}
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="rewardType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Desconto</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="percentage">Porcentagem (%)</SelectItem>
                          <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rewardValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Valor{' '}
                        <span className="text-muted-foreground font-normal">
                          ({rewardType === 'percentage' ? '%' : 'R$'})
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Safety limits */}
              <div className="grid gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="warrantyDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Garantia (dias)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="30"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 7)}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">1–30 dias</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="minOrderValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pedido Mínimo</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">R$ (0 = sem mínimo)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maxRewardValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Teto Desconto</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">R$ (0 = sem teto)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              {/* Platform config */}
              <div className="space-y-3">
                <p className="text-sm font-medium">Configurações de Plataforma</p>

                <FormField
                  control={form.control}
                  name="guruMaxSubscriptionCycles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">
                        Guru — Ciclos de assinatura
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0 = ilimitado"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-3 rounded-md border p-3">
                  <p className="text-xs font-medium text-muted-foreground">Yampi</p>

                  <FormField
                    control={form.control}
                    name="yampiOncePerCustomer"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between">
                        <FormLabel className="text-sm font-normal">Uma vez por cliente</FormLabel>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="yampiAccumulate"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between">
                        <FormLabel className="text-sm font-normal">
                          Acumular com outros cupons
                        </FormLabel>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="yampiIgnorePromotionProducts"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between">
                        <FormLabel className="text-sm font-normal">
                          Ignorar produtos em promoção
                        </FormLabel>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Active toggle */}
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div>
                      <FormLabel>Programa ativo</FormLabel>
                      <FormDescription className="text-xs">
                        Desativar impede novos creators de serem vinculados.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </SheetBody>

            <SheetFooter>
              <ChonkyButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </ChonkyButton>
              <ChonkyButton type="submit" loading={isPending}>
                {isEditing ? 'Salvar' : 'Criar Programa'}
              </ChonkyButton>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
